import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { authAPI, clearLegacyTokens } from "@/lib/api";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import AuthPage from "@/pages/AuthPage";
import ForgotPassword from "@/pages/ForgotPassword";
import UserTypeSelection from "@/pages/UserTypeSelection";
import PartnerDashboard from "@/pages/PartnerDashboard";
import UserDashboard from "@/pages/UserDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import BrowseListings from "@/pages/BrowseListings";
import ListingDetail from "@/pages/ListingDetail";
import PrivateJets from "@/pages/PrivateJets";
import JetDetail from "@/pages/JetDetail";
import LocalDelivery from "@/pages/LocalDelivery";
import TrackingPage from "@/pages/TrackingPage";
import NegotiationsPage from "@/pages/NegotiationsPage";
import BulkBooking from "@/pages/BulkBooking";
import Carpool from "@/pages/Carpool";
import CarpoolDetail from "@/pages/CarpoolDetail";
import OfferRide from "@/pages/OfferRide";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Logout error:', error.message);
      }
    }
    clearLegacyTokens();
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const authContextValue = useMemo(() => ({ 
    user, login, logout, updateUser, loading, checkAuth 
  }), [user, loading, login, logout, updateUser, checkAuth]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredType }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }

  // If user hasn't selected their type yet
  if (!user.user_type) {
    return <Navigate to="/select-type" replace />;
  }

  if (requiredType && user.user_type !== requiredType) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with OAuth callback detection
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/select-type" element={<UserTypeSelection />} />
      <Route path="/browse" element={<BrowseListings />} />
      <Route path="/listing/:id" element={<ListingDetail />} />
      <Route path="/private-jets" element={<PrivateJets />} />
      <Route path="/jet/:id" element={<JetDetail />} />
      <Route path="/local-delivery" element={<LocalDelivery />} />
      <Route path="/bulk-booking" element={<BulkBooking />} />
      <Route path="/carpool" element={<Carpool />} />
      <Route path="/carpool/:id" element={<CarpoolDetail />} />
      <Route
        path="/carpool/offer"
        element={
          <ProtectedRoute>
            <OfferRide />
          </ProtectedRoute>
        }
      />
      <Route path="/track/:deliveryId" element={<TrackingPage />} />
      <Route path="/negotiations" element={<NegotiationsPage />} />
      <Route
        path="/partner/*"
        element={
          <ProtectedRoute requiredType="partner">
            <PartnerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute requiredType="user">
            <UserDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
